import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Edit3, Trash2, Sparkles, AlertCircle, ChevronRight,
  Loader2, ChevronDown,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/authFetch";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-3 — CartaoVisual.
//
// Proporção real ISO 7810 (~1.586:1). Desktop ~360×220, mobile ~280×175.
// Background gradient escuro com cor determinística por banco.
// Hover overlay com 3 ações (edit, voz, delete). Click no corpo expande
// detalhes inline (acordeão).
//
// Lazy fetch de fatura-estimada + fatura-prediction APENAS quando expandido
// (performance — evita N requests com N cartões).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

// Palette de cor de fundo determinística por nome do banco.
const _BANK_GRADIENTS = [
  "from-[#1a0f33] via-[#2d1b5b] to-[#1e1147]",   // roxo profundo
  "from-[#0a1942] via-[#15327d] to-[#0e1e54]",   // azul C6-ish
  "from-[#3d0a1a] via-[#7d1530] to-[#54071f]",   // bordô
  "from-[#1d3a1a] via-[#2d6536] to-[#173d1d]",   // verde escuro
  "from-[#3a1d0a] via-[#6e3815] to-[#54260a]",   // marrom
  "from-[#1a1a1a] via-[#2d2d2d] to-[#0a0a0a]",   // grafite
];

function _gradientFor(name) {
  if (!name) return _BANK_GRADIENTS[0];
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % _BANK_GRADIENTS.length;
  return _BANK_GRADIENTS[h];
}

function _last4(accountId) {
  // Account não tem campo last_4_digits — usar últimos 4 chars do id como placeholder.
  // Documentado como dívida técnica (depende de Pluggy/upload manual no futuro).
  if (!accountId) return "····";
  return accountId.slice(-4).toUpperCase();
}

function _faturaMonthAbbr(dueDateIso) {
  if (!dueDateIso) return "";
  try {
    const d = new Date(dueDateIso + "T00:00:00");
    const labels = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
    return labels[d.getMonth()];
  } catch { return ""; }
}

function _venceBadgeProps(daysToDue) {
  if (daysToDue == null) return null;
  if (daysToDue < 0) {
    return { variant: "danger", text: `Atrasada há ${Math.abs(daysToDue)} dia${Math.abs(daysToDue) === 1 ? "" : "s"}` };
  }
  if (daysToDue === 0) return { variant: "danger", text: "Vence hoje" };
  if (daysToDue <= 7) return { variant: "warning", text: `Vence em ${daysToDue} dia${daysToDue === 1 ? "" : "s"}` };
  return null;
}

// SVG Mastercard simplificado.
function MastercardLogo() {
  return (
    <svg width="36" height="22" viewBox="0 0 48 30" fill="none" aria-hidden="true">
      <circle cx="18" cy="15" r="12" fill="#EB001B" opacity="0.95" />
      <circle cx="30" cy="15" r="12" fill="#F79E1B" opacity="0.95" />
      <path d="M24 6 a12 12 0 0 0 0 18 a12 12 0 0 0 0 -18" fill="#FF5F00" opacity="0.85" />
    </svg>
  );
}

// Chip dourado.
function ChipSvg() {
  return (
    <svg width="36" height="28" viewBox="0 0 48 38" aria-hidden="true">
      <rect x="2" y="2" width="44" height="34" rx="5" fill="url(#chip-grad)" stroke="#caa44b" strokeWidth="1" />
      <line x1="2"  y1="14" x2="46" y2="14" stroke="#a37a2a" strokeWidth="1" />
      <line x1="2"  y1="24" x2="46" y2="24" stroke="#a37a2a" strokeWidth="1" />
      <line x1="14" y1="2"  x2="14" y2="36" stroke="#a37a2a" strokeWidth="1" />
      <line x1="34" y1="2"  x2="34" y2="36" stroke="#a37a2a" strokeWidth="1" />
      <defs>
        <linearGradient id="chip-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f4d18a" />
          <stop offset="100%" stopColor="#b88f3a" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function ProgressBar({ pct, alertLevel }) {
  const pctClamp = Math.max(0, Math.min(100, pct || 0));
  const color = alertLevel === "critical" ? "bg-status-error"
    : alertLevel === "warning" ? "bg-status-warning"
    : "bg-brand-green";
  return (
    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
      <div
        className={cn("h-full rounded-full transition-all duration-500 ease-out", color)}
        style={{ width: `${pctClamp}%` }}
      />
    </div>
  );
}

export default function CartaoVisual({
  account,
  usage,           // { used, limit, usage_pct, alert_level }
  onEditDates,
  onDelete,
  onTalk,
  onSeeTransactions,
}) {
  const [expanded, setExpanded] = useState(false);
  const [estimada, setEstimada] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const accountId = account.id;
  const name = account.name || "Cartão";
  const missingDates = !account.closing_day || !account.due_day;
  const last4 = _last4(accountId);
  const grad = _gradientFor(account.institution || name);

  const loadDetails = useCallback(async () => {
    if (estimada || loadingDetails) return;
    setLoadingDetails(true);
    try {
      const [rE, rP] = await Promise.all([
        authFetch(`${API_URL}/api/accounts/${accountId}/fatura-estimada`),
        authFetch(`${API_URL}/api/accounts/${accountId}/fatura-prediction`),
      ]);
      if (rE.ok) setEstimada(await rE.json());
      if (rP.ok) setPrediction(await rP.json());
    } catch (_) { /* mantém null */ }
    finally { setLoadingDetails(false); }
  }, [accountId, estimada, loadingDetails]);

  // Quando expande, carrega detalhes (lazy).
  useEffect(() => {
    if (expanded) loadDetails();
  }, [expanded, loadDetails]);

  const venceBadge = _venceBadgeProps(estimada?.days_to_due);
  const faturaAtual = estimada?.fatura_atual;
  const faturaMes = _faturaMonthAbbr(estimada?.due_date);

  const usagePct = usage?.usage_pct ?? 0;
  const alertLevel = usage?.alert_level || "ok";

  return (
    <div className="relative" data-testid={`cartao-${accountId}`}>
      <motion.button
        type="button"
        onClick={() => setExpanded(e => !e)}
        whileHover={{ y: -2 }}
        className={cn(
          "relative w-full text-left rounded-2xl overflow-hidden",
          "aspect-[1.586/1]",
          "bg-gradient-to-br", grad,
          "shadow-[0_8px_24px_rgba(0,0,0,0.4)]",
          "transition-shadow duration-fast group/card",
          "hover:shadow-[0_0_0_1px_rgba(0,212,255,0.4),0_8px_32px_rgba(0,0,0,0.5)]",
        )}
        aria-expanded={expanded}
        aria-controls={`cartao-detalhes-${accountId}`}
      >
        {/* Light spot (parallax sutil) */}
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{ background: "radial-gradient(circle at 30% 0%, rgba(255,255,255,0.18), transparent 55%)" }}
          aria-hidden="true"
        />

        {/* Top row: nome banco + status badge */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
          <div className="font-display text-white text-lg md:text-xl font-bold tracking-tight truncate max-w-[60%]">
            {(account.institution || name).toUpperCase()}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {missingDates ? (
              <Badge variant="success" className="text-[10px] px-2 py-0.5 bg-brand-green/20 text-brand-green border-brand-green/40">
                Definir datas
              </Badge>
            ) : venceBadge ? (
              <Badge variant={venceBadge.variant} className="text-[10px] px-2 py-0.5">
                {venceBadge.text}
              </Badge>
            ) : null}
          </div>
        </div>

        {/* Chip + número */}
        <div className="absolute top-[44%] left-4">
          <ChipSvg />
        </div>
        <div className="absolute top-[58%] left-4 right-4 text-white/85 text-base md:text-lg font-mono tracking-[0.18em]">
          ···· ···· ···· {last4}
        </div>

        {/* Bottom-left: fatura */}
        <div className="absolute bottom-4 left-4 right-16">
          <p
            className="text-white/55 text-[10px] uppercase tracking-[0.18em] mb-0.5"
          >
            FATURA{faturaMes ? ` ${faturaMes}` : ""}
          </p>
          <p className="text-white font-display font-bold text-xl md:text-2xl truncate">
            {faturaAtual != null ? fmtBRL(faturaAtual) : (missingDates ? "—" : "…")}
          </p>
        </div>

        {/* Bottom-right: bandeira (Mastercard default) */}
        <div className="absolute bottom-4 right-4">
          <MastercardLogo />
        </div>

        {/* Hover overlay com 3 ações */}
        <div className="absolute top-3 right-3 opacity-0 group-hover/card:opacity-100 transition-opacity flex items-center gap-1">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onTalk?.(account); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onTalk?.(account); } }}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-brand-cyan/15 text-brand-cyan hover:bg-brand-cyan/30 transition-colors"
            aria-label="Falar com Nexo sobre este cartão"
            data-testid={`cartao-${accountId}-talk`}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onEditDates?.(account); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onEditDates?.(account); } }}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/10 text-white hover:bg-white/25 transition-colors"
            aria-label="Editar cartão"
            data-testid={`cartao-${accountId}-edit`}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onDelete?.(account); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDelete?.(account); } }}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-status-error/15 text-status-error hover:bg-status-error/30 transition-colors"
            aria-label="Apagar cartão"
            data-testid={`cartao-${accountId}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </span>
        </div>

        {/* Indicator chevron */}
        <ChevronDown
          className={cn(
            "absolute bottom-3 right-1/2 translate-x-1/2 w-4 h-4 text-white/30 transition-transform duration-fast",
            expanded && "rotate-180",
          )}
          aria-hidden="true"
        />
      </motion.button>

      {/* Detalhes expandidos (acordeão) */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={`cartao-detalhes-${accountId}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
            data-testid={`cartao-${accountId}-details`}
          >
            <div className="mt-3 glass-card rounded-xl p-4 space-y-3 text-caption">
              {missingDates ? (
                <div className="flex items-center gap-2 text-status-error">
                  <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span>Configure as datas pra ver fatura e estimativa.</span>
                </div>
              ) : loadingDetails && !estimada ? (
                <div className="flex items-center gap-2 text-foreground-muted">
                  <Loader2 className="w-4 h-4 animate-spin" /> carregando detalhes…
                </div>
              ) : (
                <>
                  {/* Fatura atual */}
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-muted">Fatura atual</span>
                    <span className="font-mono font-semibold text-foreground tabular-nums">
                      {estimada?.fatura_atual != null ? fmtBRL(estimada.fatura_atual) : "—"}
                    </span>
                  </div>

                  {/* Estimativa Nexo */}
                  {prediction && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-foreground-muted truncate">Estimativa do Nexo</span>
                        <Badge
                          variant={prediction.confidence === "low" ? "outline" : "info"}
                          className="text-[10px] px-1.5 py-0"
                          title={prediction.explanation}
                        >
                          ✨ {prediction.confidence === "low" ? "Baixa confiança" : "Predição"}
                        </Badge>
                      </div>
                      <span className="font-mono font-semibold text-brand-cyan tabular-nums shrink-0">
                        {fmtBRL(prediction.estimated_final)}
                      </span>
                    </div>
                  )}

                  {/* Período */}
                  {estimada?.billing_period && (
                    <div className="flex items-center justify-between text-foreground-muted">
                      <span>Período</span>
                      <span className="text-foreground">
                        {estimada.billing_period.start} → {estimada.billing_period.end}
                      </span>
                    </div>
                  )}

                  {/* Vencimento */}
                  {estimada?.due_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-muted">Vencimento</span>
                      <span className="text-foreground">
                        {estimada.due_date}
                        {typeof estimada.days_to_due === "number" && (
                          <span className="text-foreground-muted ml-1.5">
                            ({estimada.days_to_due > 0 ? `em ${estimada.days_to_due}d` : estimada.days_to_due === 0 ? "hoje" : `${Math.abs(estimada.days_to_due)}d atrás`})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Limite usado */}
              {usage && usage.limit > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-subtle/40">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-muted">Limite usado</span>
                    <span className={cn(
                      "font-semibold tabular-nums",
                      alertLevel === "critical" ? "text-status-error" :
                      alertLevel === "warning" ? "text-status-warning" :
                      "text-brand-green",
                    )}>
                      {usagePct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
                    </span>
                  </div>
                  <ProgressBar pct={usagePct} alertLevel={alertLevel} />
                  <p className="text-micro text-foreground-muted">
                    {fmtBRL(usage.used)} / {fmtBRL(usage.limit)}
                  </p>
                </div>
              )}

              {/* CTA ver transações */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onSeeTransactions?.(account); }}
                className="inline-flex items-center gap-1 text-caption font-medium text-brand-cyan hover:text-brand-cyan-strong transition-colors"
                data-testid={`cartao-${accountId}-see-tx`}
              >
                Ver transações deste cartão
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
